<template>
  <div class="m-table" :class="{ 'is-border': border }">
    <VxeTable
      :data="data"
      :height="height"
      :span-method="mergeMethod"
      :empty-text="emptyText"
      @cell-click="handleCellClick"
      header-row-class-name="header-row-class-name"
      header-cell-class-name="header-cell-class-name"
      cell-class-name="cell-class-name"
      :row-class-name="customRowClass"
      :scroll-x="{
        enabled: true
      }"
    >
      <template v-for="(column, index) in processedColumns" >
        <VxeColumn
          :field="column.prop"
          :title="column.title"
          :width="column.width"
          :min-width="column.minWidth"
          :fixed="column.fixed"
          :align="column.align || 'left'"
          :class-name="column.className"
        >
          <template #default="{ row, rowIndex }">
            <!-- 使用自定义渲染函数 -->
            <template v-if="column.renderHtml">
              <div v-html="column.renderHtml(row, rowIndex, column, index)"></div>
            </template>
            <!-- 使用自定义插槽 -->
            <slot v-else-if="column.slot" :name="column.slot" :row="row" :column="column" :index="rowIndex"></slot>
            <!-- 默认渲染 -->
            <template v-else>
              {{ row[column.prop] }}
            </template>
          </template>
        </VxeColumn>
      </template>
      
      <template #empty>
        <slot name="empty">
          <div class="m-table__empty-text">{{ emptyText }}</div>
        </slot>
      </template>
    </VxeTable>
  </div>
</template>

<script>
import { VXETable, VxeTable, VxeColumn } from 'vxe-table'
import 'vxe-table/lib/style.css'

export default {
  components: {
    VxeTable,
    VxeColumn
  },
  name: 'MTable',
  props: {
    // 表格数据，是一个数组，每个元素代表一行数据
    data: {
      type: Array,
      default: () => []
    },
    // 列配置，是一个数组，每个元素代表一列的配置信息
    // 每列配置项包含：
    // - title: 列标题
    // - prop: 对应data中的字段名
    // - width: 列宽度，如'100px'，不设置则自动分配
    // - align: 对齐方式，可选值：'left'(默认), 'center', 'right'
    // - className: 自定义类名
    // - renderHtml: 自定义渲染函数，参数为(row, rowIndex, column, colIndex)
    // - slot: 使用具名插槽渲染，值为插槽名称
    // - fixed: 是否固定列，可选值：true/'left'(左侧固定), 'right'(右侧固定)
    columns: {
      type: Array,
      default: () => []
    },
    // 表格宽度，默认100%
    width: {
      type: String,
      default: '100%'
    },
    // 表格高度，设置后内容部分会出现滚动条
    height: {
      type: [String, Number],
      default: ''
    },
    // 表头高度 (vxe-table会自动处理，此属性保留但不使用)
    headerHeight: {
      type: Number,
      default: 40
    },
    // 是否固定表头 (vxe-table会自动处理，此属性保留但不使用)
    stickyHeader: {
      type: Boolean,
      default: false
    },
    // 是否显示边框
    border: {
      type: Boolean,
      default: true
    },
    // 空数据时显示的文本
    emptyText: {
      type: String,
      default: '暂无数据'
    },
    // 自定义单元格合并方法
    // 参数为 ({row, column, rowIndex, columnIndex}) 返回 {rowspan, colspan}
    spanMethod: {
      type: Function,
      default: null
    },
    // 需要自动合并的列属性名数组，如 ['toAddress']
    // 相邻行的同列具有相同值时会自动合并
    // spanMethod 优先于 mergeProps
    mergeProps: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    // 处理列配置，确保所有必要的属性都存在
    processedColumns() {
      return this.columns.map(column => {
        // 处理对齐方式
        if (column.align === 'center') {
          column.align = 'center';
        } else if (column.align === 'right') {
          column.align = 'right';
        } else {
          column.align = 'left';
        }
        
        // 处理固定列
        if (column.fixed === true) {
          column.fixed = 'left';
        }
        
        return column;
      });
    }
  },
  methods: {
    // 单元格点击事件，转发为行点击事件以保持兼容性
    handleCellClick({ row, rowIndex }) {
      this.$emit('row-click', row, rowIndex);
    },
    customRowClass({ row, rowIndex }) {
      return 'custom-row-class'
    },
    // 单元格合并方法，适配vxe-table的span-method接口
    mergeMethod({ row, column, rowIndex, columnIndex }) {
      // 如果设置了自定义合并方法，优先使用
      if (typeof this.spanMethod === 'function') {
        return this.spanMethod({
          row,
          column,
          rowIndex,
          columnIndex
        });
      } 
      // 否则使用自动合并逻辑
      else if (this.mergeProps && this.mergeProps.length > 0) {
        const field = column.property || column.field;
        
        // 如果当前列不在需要合并的列中，不合并
        if (!this.mergeProps.includes(field)) {
          return { rowspan: 1, colspan: 1 };
        }
        
        // 获取当前单元格的值
        const currentValue = row[field];
        
        // 计算向下合并的行数
        let rowspan = 1;
        let nextRow = rowIndex + 1;
        while (nextRow < this.data.length) {
          if (this.data[nextRow][field] === currentValue) {
            rowspan++;
            nextRow++;
          } else {
            break;
          }
        }
        
        // 如果不是第一个相同值的单元格，则不显示，即隐藏重复单元格
        if (rowIndex > 0 && this.data[rowIndex - 1][field] === currentValue) {
          return { rowspan: 0, colspan: 0 };
        }
        
        return { rowspan, colspan: 1 };
      }
      
      // 默认不合并
      return { rowspan: 1, colspan: 1 };
    }
    
    // 此方法已被mergeMethod替代
  }
}
</script>

<style lang="scss" scoped>
.m-table {
  position: relative;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  border-bottom: none !important;
  ::v-deep .vxe-table--body-inner-wrapper::-webkit-scrollbar {
    width: 0px;
    height: 3px;
    // overflow: auto !important;
    // -webkit-appearance: auto !important;
    display: block !important;
    box-sizing: border-box;
  }
  ::-webkit-scrollbar-thumb {
    border-radius: 4px;
  }
  ::-webkit-scrollbar-track,
    ::-webkit-scrollbar-corner {
      background-color: #FFFFFF;
    }
  ::-webkit-scrollbar-thumb {
      background-color: #EFEFF4;
      margin-bottom: 5px;
    }
  ::-webkit-scrollbar-thumb:hover,
    ::-webkit-scrollbar-thumb:active {
      background-color: #EFEFF4;
  }
  &.is-border {
    border: 1px solid #ebedf0;
    border-radius: 2px;
  }
  ::v-deep .header-row-class-name {
    background-color:  #8FC8FF;
  }
  ::v-deep .header-cell-class-name {
    border-right: 1px solid #fff;
  }
  ::v-deep .cell-class-name {
    border-right: 1px solid #EFEFF4;
    box-sizing: border-box;
    // border-bottom: 1px solid #EFEFF4;
  }
  ::v-deep .custom-row-class {
    border-bottom: #8FC8FF !important;
  }
  table {
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
  }
  
  th, td {
    padding: 10px;
    box-sizing: border-box;
    text-align: left;
    word-break: break-all;
    border-bottom: 1px solid #ebedf0;
    
    &.is-center {
      text-align: center;
    }
    
    &.is-right {
      text-align: right;
    }
  }
  
  th {
    font-weight: 500;
    color: #323233;
    background-color: #f7f8fa;
  }
  
  &__header {
    background-color: #f7f8fa;
    overflow: hidden;
    
    &.is-sticky {
      position: sticky;
      top: 0;
      z-index: 2;
    }
    .col {
      
    }
  }
  
  &__body {
    overflow: auto;
  }
  
  &__empty {
    padding: 30px 0;
    text-align: center;
  }
  
  &__empty-text {
    color: #969799;
    font-size: 14px;
  }
}
</style>